<template>
  <div class="modal-overlay" @click.self="closeModal" @keydown.esc="closeModal">
    <div class="modal-container">
      <div class="modal-header">
        <h2>Batch Generate Embeddings</h2>
        <button class="btn-close" @click="closeModal" :disabled="loading">&times;</button>
      </div>

      <div class="modal-body">
        <!-- Info Section -->
        <div class="info-section">
          <p class="info-text">
            Generate AI embeddings for blog posts to enable similarity-based recommendations.
            Cost: ~$0.001 per post.
          </p>
          <div v-if="stats" class="stats-summary">
            <p><strong>Current Coverage:</strong> {{ stats.embedded_posts }} / {{ stats.total_posts }} posts ({{ stats.coverage_percent.toFixed(1) }}%)</p>
          </div>
        </div>

        <!-- Form Section -->
        <div v-if="!result" class="form-section">
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="forceRegenerate" :disabled="loading" />
              Force regenerate existing embeddings
            </label>
            <p class="help-text">Re-process posts that already have embeddings</p>
          </div>

          <div class="advanced-options">
            <button
              type="button"
              class="btn-link"
              @click="showAdvanced = !showAdvanced"
              :disabled="loading"
            >
              {{ showAdvanced ? '▼' : '▶' }} Advanced Options
            </button>

            <div v-if="showAdvanced" class="advanced-content">
              <div class="form-group">
                <label for="slugs">Specific Post Slugs (comma-separated)</label>
                <textarea
                  id="slugs"
                  v-model="slugsInput"
                  placeholder="post-slug-1, post-slug-2, ..."
                  rows="3"
                  :disabled="loading"
                ></textarea>
                <p class="help-text">Leave empty to process all published posts</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Processing embeddings... This may take a moment.</p>
        </div>

        <!-- Result Display -->
        <div v-if="result && !loading" class="result-section">
          <div v-if="!error && result.acknowledged" class="success-message">
            <h3>✓ Batch Generation Started</h3>
            <p class="acknowledgement-message">{{ result.message }}</p>
            <p class="instruction-text">
              The embeddings are being generated in the background.
              Check the server logs for progress and refresh the stats to see updates.
            </p>
          </div>

          <div v-else-if="!error" class="success-message">
            <h3>✓ Batch Generation Complete</h3>
            <div class="result-stats">
              <p><strong>Total Processed:</strong> {{ result.total_processed }}</p>
              <p><strong>Successful:</strong> {{ result.successful }}</p>
              <p><strong>Failed:</strong> {{ result.failed }}</p>
              <p v-if="result.total_cost !== undefined"><strong>Total Cost:</strong> ${{ result.total_cost.toFixed(4) }}</p>
              <p v-if="result.duration_seconds !== undefined"><strong>Duration:</strong> {{ result.duration_seconds.toFixed(1) }}s</p>
              <p v-if="result.embedding_model"><strong>Model:</strong> {{ result.embedding_model }}</p>
            </div>
            <div v-if="result.failed > 0" class="failed-posts">
              <p><strong>Failed Posts:</strong></p>
              <ul>
                <li v-for="slug in result.failed_slugs" :key="slug">{{ slug }}</li>
              </ul>
            </div>
          </div>

          <div v-else class="error-message">
            <h3>✗ Generation Failed</h3>
            <p>{{ error }}</p>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          v-if="!result"
          class="btn btn-secondary"
          @click="closeModal"
          :disabled="loading"
        >
          Cancel
        </button>
        <button
          v-if="result"
          class="btn btn-primary"
          @click="closeModal"
        >
          Close
        </button>
        <button
          v-if="!result"
          class="btn btn-primary"
          @click="runBatch"
          :disabled="loading"
        >
          <span v-if="!loading">Run Batch</span>
          <span v-else>Processing...</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useEmbeddings } from '@/services/embeddings';

export default {
  name: 'EmbeddingBatchModal',
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const { getEmbeddingStats, batchGenerateEmbeddings } = useEmbeddings();

    const stats = ref(null);
    const forceRegenerate = ref(false);
    const slugsInput = ref('');
    const showAdvanced = ref(false);
    const loading = ref(false);
    const result = ref(null);
    const error = ref('');

    const loadStats = async () => {
      try {
        stats.value = await getEmbeddingStats();
      } catch (err) {
        console.error('Failed to load embedding stats:', err);
      }
    };

    const closeModal = () => {
      if (!loading.value) {
        emit('close');
      }
    };

    const runBatch = async () => {
      if (loading.value) return;

      loading.value = true;
      error.value = '';
      result.value = null;

      try {
        // Parse slugs from textarea
        const slugs = slugsInput.value
          .split(',')
          .map(s => s.trim())
          .filter(s => s.length > 0);

        const options = {
          force_regenerate: forceRegenerate.value,
          post_slugs: slugs,
        };

        const batchResult = await batchGenerateEmbeddings(options);
        result.value = batchResult;
        emit('success', batchResult);
      } catch (err) {
        error.value = err.message || 'Failed to generate embeddings';
        result.value = { failed: true };
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadStats();
    });

    return {
      stats,
      forceRegenerate,
      slugsInput,
      showAdvanced,
      loading,
      result,
      error,
      closeModal,
      runBatch,
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1f2937;
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.btn-close:hover:not(:disabled) {
  background-color: #f3f4f6;
  color: #1f2937;
}

.btn-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 1.5rem;
}

.info-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #eff6ff;
  border-left: 4px solid #3b82f6;
  border-radius: 4px;
}

.info-text {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
}

.stats-summary {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #bfdbfe;
}

.stats-summary p {
  margin: 0;
  color: #1f2937;
}

.form-section {
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: normal;
}

.checkbox-label input[type="checkbox"] {
  margin-right: 0.5rem;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.help-text {
  margin: 0.25rem 0 0 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.advanced-options {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-link:hover:not(:disabled) {
  color: #2563eb;
  text-decoration: underline;
}

.btn-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.advanced-content {
  margin-top: 1rem;
  padding-left: 1rem;
}

textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.875rem;
  resize: vertical;
}

textarea:focus {
  outline: none;
  border-color: #3b82f6;
  ring: 2px;
  ring-color: #bfdbfe;
}

textarea:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.loading-state {
  text-align: center;
  padding: 2rem;
}

.spinner {
  border: 4px solid #f3f4f6;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.result-section {
  padding: 1rem;
  border-radius: 4px;
}

.success-message {
  background-color: #d1fae5;
  padding: 1rem;
  border-radius: 4px;
  border-left: 4px solid #10b981;
}

.success-message h3 {
  margin: 0 0 1rem 0;
  color: #065f46;
}

.acknowledgement-message {
  margin: 0 0 1rem 0;
  color: #065f46;
  font-weight: 500;
}

.instruction-text {
  margin: 0;
  color: #047857;
  font-size: 0.9rem;
  line-height: 1.5;
}

.result-stats p {
  margin: 0.5rem 0;
  color: #065f46;
}

.failed-posts {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #a7f3d0;
}

.failed-posts ul {
  margin: 0.5rem 0 0 1.5rem;
  color: #b91c1c;
}

.error-message {
  background-color: #fee2e2;
  padding: 1rem;
  border-radius: 4px;
  border-left: 4px solid #ef4444;
}

.error-message h3 {
  margin: 0 0 0.5rem 0;
  color: #991b1b;
}

.error-message p {
  margin: 0;
  color: #991b1b;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
